/**
 * src/utils/chunkReload.js
 *
 * The one recovery path for a JavaScript chunk that failed to load (GH #7430).
 *
 * Two places see a chunk failure: ErrorBoundary (a lazy route whose import
 * rejected inside React) and the global `error` / `unhandledrejection`
 * listeners in main.jsx (a failure that reaches the window before, or instead
 * of, React). Both used to reload the page — but only ErrorBoundary capped it.
 * main.jsx reloaded on every chunk error with no limit, so a chunk fetch that
 * kept stalling (lazyWithTimeout gives up after 15s) reloaded the app on a
 * cycle: the customer saw a blank page, no message and no way out, which is
 * exactly what the 2026-09-30 TPV run recorded on five routes.
 *
 * Both callers now share this guard: at most MAX_AUTO_RELOADS reloads inside
 * RELOAD_WINDOW_MS (one fixes a stale chunk after a deploy outright), and once
 * the cap is spent the failure is shown, with a Reload button, instead.
 */

export const RELOAD_GUARD_KEY = 'tryggr:chunk-reload';
export const MAX_AUTO_RELOADS = 2;
export const RELOAD_WINDOW_MS = 60_000;
export const NOTICE_ID = 'tryggr-chunk-failure-notice';

// One page life, one reload, however many listeners or render passes call in.
let reloadScheduled = false;

export function isChunkLoadError(message) {
  const msg = typeof message === 'string' ? message : '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed')
  );
}

/** @returns {boolean} true if a reload was triggered (or already is), false if the cap is spent. */
export function tryAutoReload(reload = () => window.location.reload()) {
  if (reloadScheduled) return true;

  let record = { count: 0, firstAt: 0 };
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (raw) record = JSON.parse(raw) || record;
  } catch {
    // sessionStorage blocked or the value is corrupt. Treat it as a first
    // attempt; the in-memory flag above still stops this page looping.
    record = { count: 0, firstAt: 0 };
  }

  const now = Date.now();
  if (!record.firstAt || now - record.firstAt > RELOAD_WINDOW_MS) {
    record = { count: 0, firstAt: now };
  }
  if (record.count >= MAX_AUTO_RELOADS) return false;

  record.count += 1;
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify(record));
  } catch {
    // Best effort — without persistence the cap degrades to one reload per
    // page life via reloadScheduled, which is still bounded.
  }

  reloadScheduled = true;
  reload();
  return true;
}

/**
 * A visible, actionable notice for a chunk failure the reload cap has given
 * up on. Plain DOM rather than React: the failure may have left nothing of
 * the React tree on screen. Idempotent — one notice however often it fires.
 */
export function showChunkFailureNotice(doc = document) {
  if (!doc || !doc.body || doc.getElementById(NOTICE_ID)) return;
  const box = doc.createElement('div');
  box.id = NOTICE_ID;
  box.setAttribute('role', 'alert');
  box.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'right:0',
    'z-index:99999',
    'padding:14px 24px',
    'display:flex',
    'align-items:center',
    'justify-content:space-between',
    'gap:12px',
    'font-size:13px',
    'background:var(--bg2)',
    'color:var(--fg)',
    'border-bottom:2px solid var(--danger)',
  ].join(';');

  const text = doc.createElement('span');
  text.textContent =
    'Part of the app could not be loaded — the connection to the server stalled. ' +
    'Reload to try again.';

  const button = doc.createElement('button');
  button.type = 'button';
  button.textContent = 'Reload';
  button.className = 'btn';
  button.addEventListener('click', () => {
    try {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch {
      /* a manual reload is the user's choice — never blocked by storage */
    }
    window.location.reload();
  });

  box.append(text, button);
  doc.body.appendChild(box);
}

/** Test seam: reset the in-memory once-per-page flag. */
export function __resetChunkReloadForTests() {
  reloadScheduled = false;
}
